
import React, { useEffect, useRef, useState } from "react";
import Layout from "../core/Layout";
import { useListSettings, useUpdateSetting } from "./api/setting";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import showToast from "../utils/toast";

const PrintDetails = () => {
    const canvasRef = useRef(null);
    const [text, setText] = useState("");
    const [alignment, setAlignment] = useState("left");
    const [headerImage, setHeaderImage] = useState(null);
    const [footerImage, setFooterImage] = useState(null);
    const [signatureImage, setSignatureImage] = useState(null);
    const [headerFile, setHeaderFile] = useState(null);
    const [footerFile, setFooterFile] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [textSize, setTextSize] = useState(20);
    const [footerNote, setFooterNote] = useState("");
    const ref = useRef();

    const navigate = useNavigate();
    const updateSettingMutation = useUpdateSetting();

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const { data: settings } = useListSettings();

    useEffect(() => {
        if (!userInfo) {
            navigate("/signin");
        }
    }, [userInfo, navigate]);

    const reset = () => {
        ref.current.value = "";
    };

    const appointment = {
        patientName: "John Doe",
        doctorName: "Dr. Smith",
        date: "2025-03-12",
        time: "10:30 AM",
        reason: "Regular Checkup",
        notes: "Patient has mild fever. Prescribed medication.",
    };

    useEffect(() => {
        if (settings?.data?.[selectedCategory]) {
            const categorySettings = settings.data[selectedCategory];
            setText(settings.data.printText || "");
            setHeaderImage(categorySettings.header || null);
            setFooterImage(categorySettings.footer || null);
            setSignatureImage(categorySettings.signature || null);
            setTextSize(settings.data.textSize || 18);
            setFooterNote(categorySettings.footerNote || "");
        } else {
            setText("");
            setHeaderImage(null);
            setFooterImage(null);
            setSignatureImage(null);
             setFooterNote("");
        }

        setHeaderFile(null);
        setFooterFile(null);
        setSignatureFile(null);
        ref.current.value = "";
    }, [selectedCategory, settings]);

    useEffect(() => {
        updateCanvas();
    }, [text, headerImage, footerImage, signatureImage, alignment, textSize, footerNote]);

    const updateCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const context = canvas.getContext("2d");
            const scale = window.devicePixelRatio || 1;
            canvas.width = 600 * scale;
            canvas.height = 800 * scale;
            canvas.style.width = "600px";
            canvas.style.height = "800px";
            context.scale(scale, scale);
            context.clearRect(0, 0, canvas.width / scale, canvas.height / scale);

            // Draw header image
            if (headerImage) {
                const imgHeader = new Image();
                imgHeader.onload = () => {
                    const aspectRatio = imgHeader.height / imgHeader.width;
                    const headerHeight = 600 * aspectRatio;
                    context.drawImage(imgHeader, 0, 0, 600, headerHeight);
                };
                imgHeader.src = headerImage;
            }

            context.fillStyle = "#000000";
            context.font = `16px Roboto, sans-serif`;
            context.textAlign = alignment;

            // Draw main text
            const textYPosition = canvas.height / scale - 100;
            const textXPosition =
                alignment === "left"
                    ? 50
                    : alignment === "center"
                    ? canvas.width / (2 * scale)
                    : canvas.width / scale - 50;

            // context.fillText(text, textXPosition, textYPosition);
            context.font = `${textSize}px Roboto, sans-serif`;

            // Draw appointment details
            let yPosition = 400;

            const renderText = (label, value) => {
                context.fillText(`${label}: ${value}`, 50, yPosition);
                yPosition += textSize + 10;
            };

            renderText("Patient", appointment.patientName);
            renderText("Doctor", appointment.doctorName);
            renderText("Date", appointment.date);
            renderText("Time", appointment.time);
            renderText("Reason", appointment.reason);
            renderText("Notes", appointment.notes);
            

            // renderText("Dynamic Notes",text);


            // Draw footer and signature
            if (footerImage) {
                const imgFooter = new Image();
                imgFooter.onload = () => {
                    const aspectRatioFooter = imgFooter.height / imgFooter.width;
                    const footerHeight = 600 * aspectRatioFooter;
                    const yPosFooter = canvas.height / scale - footerHeight;

                    // Draw footer note above footer image
                    context.fillStyle = "#000000";
                    context.font = `${textSize}px Roboto, sans-serif`;
                    context.textAlign = "center";
                    if(footerNote?.trim()) {
                        context.fillText("Note: " + footerNote, canvas.width / (2 * scale), yPosFooter - 10);
                    }

                    if (signatureImage) {
                        const imgSign = new Image();
                        imgSign.onload = () => {
                            const signWidth = 150;
                            const signHeight = (imgSign.height / imgSign.width) * signWidth;
                            const signX = (canvas.width / (2 * scale)) - signWidth / 2;
                            const signY = yPosFooter - signHeight - 30; // above footer note

                            context.drawImage(imgSign, signX, signY, signWidth, signHeight);
                            context.drawImage(imgFooter, 0, yPosFooter, 600, footerHeight);
                        };
                        imgSign.src = signatureImage;
                    } else {
                        context.drawImage(imgFooter, 0, yPosFooter, 600, footerHeight);
                    }
                };
                imgFooter.src = footerImage;
            } else if (signatureImage) {
                // If footer image missing but signature exists, draw signature at bottom
                const imgSignOnly = new Image();
                imgSignOnly.onload = () => {
                    const signWidth = 150;
                    const signHeight = (imgSignOnly.height / imgSignOnly.width) * signWidth;
                    const signX = (canvas.width / (2 * scale)) - signWidth / 2;
                    const signY = canvas.height / scale - signHeight - 50;

                    context.drawImage(imgSignOnly, signX, signY, signWidth, signHeight);
                };
                imgSignOnly.src = signatureImage;
            }
        }
    };

    const handleImageUpload = (e, setImage, setFile) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const options = {
            header: headerFile,
            footer: footerFile,
            signature: signatureFile,
        
            printText: footerNote,
            text,
            textSize,
            footerNote,
        };

        updateSettingMutation.mutate({ category: selectedCategory, setting: options });
        showToast("Form submitted successfully");
        reset();
    };

    return (
        <Layout>
            <div style={styles.pageContainer}>
                <h1 style={styles.pageTitle}>Print Details</h1>
                <div style={styles.container}>
                    <div style={styles.formSection}>
                        <div style={styles.formCard}>
                            <h2 style={styles.formTitle}>Configuration</h2>
                            <form onSubmit={handleSubmit} style={styles.form}>
                                <div style={styles.inputGroup}>
                                    <label htmlFor="category" style={styles.label}>Category</label>
                                    <select
                                        id="category"
                                        style={styles.select}
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = "#e2e8f0";
                                            e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                                        }}
                                    >
                                        <option value={""}>Select Category</option>
                                        <option value="forms">Forms</option>
                                        <option value="prescription">Prescription</option>
                                        <option value="patient">Patient</option>
                                        <option value="expense">Expense</option>
                                    </select>
                                </div>

                                <div style={styles.inputGroup}>
                                    <label htmlFor="headerImage" style={styles.label}>Header Image</label>
                                    <input
                                        id="headerImage"
                                        type="file"
                                        ref={ref}
                                        style={styles.fileInput}
                                        accept="image/*"
                                        onChange={(e) =>
                                            handleImageUpload(e, setHeaderImage, setHeaderFile)
                                        }
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label htmlFor="footerImage" style={styles.label}>Footer Image</label>
                                    <input
                                        id="footerImage"
                                        type="file"
                                        style={styles.fileInput}
                                        accept="image/*"
                                        ref={ref}
                                        onChange={(e) => {
                                            setFooterFile(null);
                                            handleImageUpload(e, setFooterImage, setFooterFile);
                                        }}
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label htmlFor="signatureImage" style={styles.label}>Signature Image</label>
                                    <input
                                        id="signatureImage"
                                        type="file"
                                        accept="image/*"
                                        style={styles.fileInput}
                                        onChange={(e) =>
                                            handleImageUpload(e, setSignatureImage, setSignatureFile)
                                        }
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Text Size: <span style={styles.textSizeValue}>{textSize}px</span></label>
                                    <input
                                        type="range"
                                        min="14"
                                        max="25"
                                        value={textSize}
                                        onChange={(e) => setTextSize(Number(e.target.value))}
                                        style={styles.slider}
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label htmlFor="footerNote" style={styles.label}>Note</label>
                                    <input
                                        id="footerNote"
                                        type="text"
                                        style={styles.input}
                                        value={footerNote}
                                        onChange={(e) => setFooterNote(e.target.value)}
                                        placeholder="Enter note"
                                        onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = "#e2e8f0";
                                            e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                                        }}
                                    />
                                </div>

                                <button 
                                    type="submit"
                                    style={styles.submitButton}
                                    onMouseEnter={(e) => Object.assign(e.target.style, styles.submitButtonHover)}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = "translateY(0)";
                                        e.target.style.boxShadow = "0 4px 16px rgba(102, 126, 234, 0.3)";
                                    }}
                                >
                                    Submit Configuration
                                </button>
                            </form>
                        </div>
                    </div>

                    <div style={styles.canvasSection}>
                        <div style={styles.canvasCard}>
                            <h2 style={styles.canvasTitle}>Preview</h2>
                            <div style={styles.canvasContainer}>
                                <canvas
                                    ref={canvasRef}
                                    width={600}
                                    height={800}
                                    style={styles.canvas}
                                ></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const styles = {
    pageContainer: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
        padding: "20px",
    },
    pageTitle: {
        textAlign: "center",
        color: "#4a5568",
        marginBottom: "30px",
        fontSize: "32px",
        fontWeight: "700",
        background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
    },
    container: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: "30px",
        maxWidth: "1400px",
        margin: "0 auto",
        flexWrap: "wrap",
    },
    formSection: {
        flex: "1",
        minWidth: "350px",
        maxWidth: "450px",
    },
    formCard: {
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        padding: "30px",
        boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15)",
        border: "1px solid #e2e8f0",
    },
    formTitle: {
        fontSize: "24px",
        fontWeight: "600",
        color: "#4a5568",
        marginBottom: "25px",
        textAlign: "center",
        background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    inputGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    label: {
        fontSize: "14px",
        fontWeight: "600",
        color: "#4a5568",
        marginBottom: "4px",
    },
    input: {
        padding: "12px 16px",
        border: "2px solid #e2e8f0",
        borderRadius: "8px",
        fontSize: "14px",
        transition: "all 0.3s ease",
        backgroundColor: "#fafbfc",
        color: "#4a5568",
        outline: "none",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    select: {
        padding: "12px 16px",
        border: "2px solid #e2e8f0",
        borderRadius: "8px",
        fontSize: "14px",
        transition: "all 0.3s ease",
        backgroundColor: "#fafbfc",
        color: "#4a5568",
        outline: "none",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
    },
    fileInput: {
        padding: "12px 16px",
        border: "2px solid #e2e8f0",
        borderRadius: "8px",
        fontSize: "14px",
        transition: "all 0.3s ease",
        backgroundColor: "#fafbfc",
        color: "#4a5568",
        outline: "none",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
    },
    inputFocus: {
        borderColor: "rgb(102, 126, 234)",
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
    },
    textSizeValue: {
        color: "rgb(102, 126, 234)",
        fontWeight: "700",
    },
    slider: {
        appearance: "none",
        width: "100%",
        height: "8px",
        borderRadius: "4px",
        background: "linear-gradient(to right, rgb(102, 126, 234), rgb(118, 75, 162))",
        outline: "none",
        cursor: "pointer",
    },
    submitButton: {
        padding: "14px 28px",
        background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
        color: "white",
        border: "none",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
        marginTop: "10px",
    },
    submitButtonHover: {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    },
    canvasSection: {
        flex: "1",
        minWidth: "650px",
        maxWidth: "700px",
    },
    canvasCard: {
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        padding: "30px",
        boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15)",
        border: "1px solid #e2e8f0",
    },
    canvasTitle: {
        fontSize: "24px",
        fontWeight: "600",
        color: "#4a5568",
        marginBottom: "25px",
        textAlign: "center",
        background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
    },
    canvasContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        backgroundColor: "#fafbfc",
        borderRadius: "12px",
        border: "2px solid #e2e8f0",
    },
    canvas: {
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#ffffff",
        maxWidth: "100%",
        height: "auto",
    },
};

export default PrintDetails;